import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from './useAuth';

export type Comment = {
  id: string;
  content: string;
  created_at: string;
  updated_at: string;
  parent_id: string | null;
  user: {
    id: string;
    username: string;
    avatar_url: string | null;
  };
  replies?: Comment[];
};

export function useComments(ideaIdOrSlug: string, ideaAuthorId: string) {
  const [comments, setComments] = useState<Comment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [ideaId, setIdeaId] = useState<string | null>(null);
  const { user } = useAuth();

  // First, resolve the idea ID if we're given a slug
  useEffect(() => {
    const resolveIdeaId = async () => {
      try {
        // Check if the input is a UUID
        const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
        if (uuidRegex.test(ideaIdOrSlug)) {
          setIdeaId(ideaIdOrSlug);
          return;
        }

        // Look up the idea ID from the slug
        const { data: resolvedId, error: lookupError } = await supabase
          .rpc('find_book_idea_by_slug', {
            search_slug: ideaIdOrSlug
          });

        if (lookupError) throw lookupError;
        setIdeaId(resolvedId);
      } catch (err) {
        console.error('Error resolving idea ID:', err);
        setError(err instanceof Error ? err.message : 'Failed to resolve idea ID');
        setIdeaId(null);
      }
    };

    resolveIdeaId();
  }, [ideaIdOrSlug]);

  const organizeComments = (flatComments: Comment[]): Comment[] => {
    const commentMap = new Map<string, Comment>();
    const topLevelComments: Comment[] = [];

    // First pass: Create a map of all comments
    flatComments.forEach(comment => {
      commentMap.set(comment.id, { ...comment, replies: [] });
    });

    // Second pass: Organize into threads
    flatComments.forEach(comment => {
      const commentWithReplies = commentMap.get(comment.id)!;
      if (comment.parent_id) {
        const parent = commentMap.get(comment.parent_id);
        if (parent) {
          parent.replies?.push(commentWithReplies);
        }
      } else {
        topLevelComments.push(commentWithReplies);
      }
    });

    return topLevelComments;
  };

  useEffect(() => {
    if (!ideaId) return;
    fetchComments();
  }, [ideaId]);

  async function fetchComments() {
    try {
      const { data, error } = await supabase
        .from('comments')
        .select(`
          *,
          user:profiles!comments_user_id_fkey(id, username, avatar_url)
        `)
        .eq('idea_id', ideaId)
        .order('created_at', { ascending: true });

      if (error) throw error;

      setComments(organizeComments(data));
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch comments');
      setComments([]);
    } finally {
      setLoading(false);
    }
  }

  const getCurrentIdentity = async () => {
    const currentIdentityId = localStorage.getItem('current_identity_id');
    
    if (!currentIdentityId) {
      // Return main profile if no pen name is active
      const { data: profile } = await supabase
        .from('profiles')
        .select('id, username, avatar_url')
        .eq('id', user!.id)
        .single();
      
      return profile;
    }

    // Get pen name profile
    const { data: penName } = await supabase
      .from('pen_names')
      .select('id, username, avatar_url')
      .eq('id', currentIdentityId)
      .single();

    return penName;
  };

  const addComment = async (content: string, parentId: string | null = null) => {
    if (!user || !ideaId) return;

    try {
      const identity = await getCurrentIdentity();
      if (!identity) throw new Error('Failed to get current identity');

      const { error } = await supabase
        .from('comments')
        .insert({
          idea_id: ideaId,
          user_id: user.id,
          content,
          parent_id: parentId,
          identity_id: identity.id,
          identity_username: identity.username,
          identity_avatar_url: identity.avatar_url
        });

      if (error) throw error;

      await fetchComments();
    } catch (err) {
      console.error('Error adding comment:', err);
      throw err;
    }
  };

  const updateComment = async (commentId: string, content: string) => {
    if (!user || !ideaId) return;

    try {
      const { error } = await supabase
        .from('comments')
        .update({ 
          content,
          updated_at: new Date().toISOString()
        })
        .eq('id', commentId)
        .eq('user_id', user.id);

      if (error) throw error;

      await fetchComments();
    } catch (err) {
      console.error('Error updating comment:', err);
      throw err;
    }
  };

  const deleteComment = async (commentId: string) => {
    if (!user) return;

    try {
      // First verify if the user can delete this comment
      const { data: comment, error: fetchError } = await supabase
        .from('comments')
        .select('user_id')
        .eq('id', commentId)
        .single();

      if (fetchError) throw fetchError;

      // Only allow deletion if user is comment author or idea author
      if (comment.user_id !== user.id && user.id !== ideaAuthorId) {
        throw new Error('You do not have permission to delete this comment');
      }

      // Delete the comment
      const { error: deleteError } = await supabase
        .from('comments')
        .delete()
        .eq('id', commentId);

      if (deleteError) throw deleteError;

      // If deletion was successful, refresh comments
      await fetchComments();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to delete comment';
      console.error('Error deleting comment:', message);
      throw new Error(message);
    }
  };

  const canDeleteComment = (commentUserId: string) => {
    if (!user) return false;
    return user.id === commentUserId || user.id === ideaAuthorId;
  };

  return { 
    comments, 
    loading: loading || !ideaId, 
    error, 
    addComment, 
    updateComment, 
    deleteComment,
    canDeleteComment 
  };
}